/* eslint-disable import/prefer-default-export */
import moment from 'moment';
import { SortBy, SortOrder } from '../models/SortOrder';
import {
  BillSponsor,
  BillDetailed,
  Bill,
  Hearing,
} from '@enview/interface/types/bills/Bill';
import { OptionType } from '../scenes/Shared/DropDowns/SingleSelectDropDown';
import { AdvFilterValue } from '@enview/interface/enum';
import CustomField from '@enview/interface/types/CustomFields';
import { BILL_TABLE_HEADERS } from './constants';
import {
  DefaultBillTableColumns,
  PUBLIC_VIEW_EXCLUDED_COLUMNS,
  REQUIRED_COLUMNS,
} from '@enview/interface/types/Bill';

export const getJurisdiction = (bill: {
  session: { state: { abbreviation: string } };
}) => {
  const jurisdiction =
    bill.session.state.abbreviation === 'usa'
      ? 'Federal'
      : bill.session.state.abbreviation.toUpperCase();
  return jurisdiction;
};

const getLastUpdatedTime = (bill: any) => {
  const actionDates = bill.actions
    ? bill.actions.map((action: any) => action.actionDate)
    : [bill.updatedAt];
  const annotationDates = bill.annotations
    ? bill.annotations.map((annotation: any) => annotation.createdAt)
    : [];
  const usersTrackingDates = bill.usersTracking
    ? bill.usersTracking.map((usersTracking: any) => usersTracking.startedAt)
    : [];
  const dates = [...actionDates, ...annotationDates, ...usersTrackingDates].filter(
    (date) => date !== undefined,
  );
  return Math.max(...dates.map((time) => parseInt(moment(time).format('x'), 10)));
};

const sortBillByRecentlyUpdated = (bills: Bill[], isReversed: boolean) => {
  if (!bills.length) {
    return [];
  }

  const billWithMostRecentUpdate = bills.map((bill) => {
    const latestUpdatedTime = getLastUpdatedTime(bill);
    return {
      ...bill,
      latestUpdatedTime,
    };
  });

  if (isReversed) {
    return billWithMostRecentUpdate.sort((a, b) => {
      if (a.latestUpdatedTime < b.latestUpdatedTime) return -1;
      if (a.latestUpdatedTime > b.latestUpdatedTime) return 1;
      return 0;
    });
  }
  return billWithMostRecentUpdate.sort((a, b) => {
    if (a.latestUpdatedTime < b.latestUpdatedTime) return 1;
    if (a.latestUpdatedTime > b.latestUpdatedTime) return -1;
    return 0;
  });
};

export const sortBillByAlpha = (bills: BillDetailed[], isReversed: boolean) => {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return bills.sort((a, b) => {
    if (isReversed) {
      return collator.compare(
        `${getJurisdiction(b)}${b.alphanumSort}`,
        `${getJurisdiction(a)}${a.alphanumSort}`,
      );
    }
    return collator.compare(
      `${getJurisdiction(a)}${a.alphanumSort}`,
      `${getJurisdiction(b)}${b.alphanumSort}`,
    );
  });
};

export const sortBillsBySortLabel = (bills: Bill[], sortOrder: SortOrder) => {
  if (!sortOrder) {
    return sortBillByAlpha(bills as BillDetailed[], false);
  }
  const { sortBy, isReversed } = sortOrder;
  switch (sortBy) {
    case SortBy.RECENT:
      return sortBillByRecentlyUpdated(bills, isReversed);
    case SortBy.ALPHANUMERICAL:
    default:
      return sortBillByAlpha(bills as BillDetailed[], isReversed);
  }
};

export const sortArrayObjectByPropertyAsce = (object: any[], property: string) => {
  return object.sort((a, b) => {
    if (a[property] < b[property]) return -1;
    if (a[property] > b[property]) return 1;
    return 0;
  });
};

export const sortArrayObjectByPropertyDesc = (object: any[], property: string) => {
  return object.sort((a, b) => {
    if (a[property] > b[property]) return -1;
    if (a[property] < b[property]) return 1;
    return 0;
  });
};

export const copyElement = (elementId: string) => {
  const selection = window.getSelection();
  if (selection) {
    const range = document.createRange();
    range.selectNode(document.getElementById(elementId) as Node);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
    selection.removeAllRanges();
    return true;
  }
  return false;
};

export const partyString = (billSponsor: BillSponsor): string => {
  if (
    !billSponsor?.person?.electedPositions ||
    billSponsor.person.electedPositions.length === 0 ||
    !billSponsor.person.electedPositions[0]?.party
  ) {
    return '';
  }
  return ` (${billSponsor.person.electedPositions[0].party.slice(0, 1).toUpperCase()})`;
};

export const democratPartyIdentifier = ' (D)';
export const republicanPartyIdentifier = ' (R)';

export const advancedFilterOptions: OptionType<AdvFilterValue>[] = [
  {
    label: 'Any of Selected',
    value: AdvFilterValue.Any,
  },
  {
    label: 'All of Selected',
    value: AdvFilterValue.All,
  },
  {
    label: 'None of Selected',
    value: AdvFilterValue.None,
  },
];

export const splitByDate = (events: Hearing[], referenceDate?: string | Date) => {
  let splitPoint: number;

  if (referenceDate) {
    splitPoint = new Date(referenceDate).getTime();
  } else {
    splitPoint = new Date().getTime();
  }

  const upcomingEvents = [];
  const pastEvents = [];

  for (const event of events) {
    const eventTime = new Date(event.start).getTime();
    if (eventTime > splitPoint) {
      upcomingEvents.push(event);
    } else {
      pastEvents.push(event);
    }
  }

  return { upcomingEvents, pastEvents };
};

export const getUserTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone;
  } catch (_err) {
    return 'America/New_York';
  }
};

export const buildColumnOptions = (
  customFields: CustomField[],
  onlyCustom: boolean = false,
  editingPublicConfig: boolean = false,
  extraColumnHeaders: Partial<Record<string, string>> = {},
  // Latest Key Action only renders when the underlying table actually supports it
  // (currently only the Illinois-only search results table) — callers whose table
  // doesn't render this column, or whose current search isn't Illinois-only, should
  // leave this false so the option isn't offered somewhere it can't be shown.
  includeLatestKeyAction: boolean = false,
  // Workspaces/Workflow Status are gated behind BillTriageWorkspaceColumns — callers
  // should pass the caller's flag state so the picker doesn't offer columns the table
  // won't actually render for that user.
  canViewWorkspaceColumns: boolean = false,
): OptionType<string>[] => {
  const defaultColumns = { ...BILL_TABLE_HEADERS, ...extraColumnHeaders };
  const excludedValues = PUBLIC_VIEW_EXCLUDED_COLUMNS as readonly string[];
  const workspaceGatedColumns: readonly string[] = [
    DefaultBillTableColumns.WORKSPACES,
    DefaultBillTableColumns.WORKFLOW_STATUS,
  ];
  const baseOptions = Object.entries(defaultColumns)
    .filter(([key]) => !editingPublicConfig || !excludedValues.includes(key))
    .filter(
      ([key]) =>
        includeLatestKeyAction || key !== DefaultBillTableColumns.LATEST_KEY_ACTION,
    )
    .filter(([key]) => canViewWorkspaceColumns || !workspaceGatedColumns.includes(key))
    .map(([key, label]) => {
      if (label === defaultColumns[DefaultBillTableColumns.LATEST_KEY_ACTION]) {
        return {
          value: key,
          label: label + ' (IL only)',
        };
      }
      return {
        value: key,
        label,
        isRequired: REQUIRED_COLUMNS.includes(key as DefaultBillTableColumns),
      };
    });

  // System fields (Workflow Status, Priority, Assignee) have their own dedicated
  // column options in defaultColumns above; excluded here to avoid listing them twice.
  // For public view config, only include custom fields marked as public.
  const filteredCustomFields = customFields
    .filter((field) => !field.isSystem)
    .filter((field) => !editingPublicConfig || field.isPublic);

  const customOptions = filteredCustomFields.map((field) => ({
    value: field.title,
    label: field.title,
    isCustom: true,
  }));

  return onlyCustom ? customOptions : [...baseOptions, ...customOptions];
};
